type ScrollKeyEvent = {
    readonly altKey: boolean;
    readonly ctrlKey: boolean;
    readonly key: string;
    readonly metaKey: boolean;
};

type ViewportCache = { current: HTMLElement | null };

const ARROW_SCROLL_STEP = 80;

const canScrollVertically = (element: HTMLElement): boolean => {
    if (element.scrollHeight <= element.clientHeight + 1) {
        return false;
    }

    const overflowY = window.getComputedStyle(element).overflowY;

    return overflowY === 'auto' || overflowY === 'scroll';
};

const findScrollViewport = (root: HTMLElement): HTMLElement | null => {
    const radixViewport = root.querySelector<HTMLElement>('[data-scroll-area-viewport]');

    if (radixViewport !== null && canScrollVertically(radixViewport)) {
        return radixViewport;
    }

    return [...root.querySelectorAll<HTMLElement>('*')].find(canScrollVertically) ?? null;
};

const scrollDeltaForKey = (key: string, viewport: HTMLElement): number | undefined => {
    const pageStep = viewport.clientHeight * 0.9;

    switch (key) {
        case 'ArrowDown':
            return ARROW_SCROLL_STEP;
        case 'ArrowUp':
            return -ARROW_SCROLL_STEP;
        case 'PageDown':
            return pageStep;
        case 'PageUp':
            return -pageStep;
        case 'End':
            return viewport.scrollHeight;
        case 'Home':
            return -viewport.scrollHeight;
        default:
            return undefined;
    }
};

export const scrollTileViewport = (
    root: HTMLElement | null,
    event: ScrollKeyEvent,
    viewportCache: ViewportCache,
): boolean => {
    if (root === null || event.ctrlKey || event.metaKey || event.altKey) {
        return false;
    }

    const cached = viewportCache.current;
    const viewport =
        cached?.isConnected === true && root.contains(cached) && canScrollVertically(cached)
            ? cached
            : findScrollViewport(root);
    viewportCache.current = viewport;

    if (viewport === null) {
        return false;
    }

    const delta = scrollDeltaForKey(event.key, viewport);

    if (delta === undefined) {
        return false;
    }

    viewport.scrollBy({ behavior: 'auto', top: delta });

    return true;
};
